import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import {
  DashpivotEvent,
  EventNotifierService,
  EventTypes,
  SignUpSteps,
} from '@site-mate/dashpivot-shared-library';

import { SignUpTemplatesModalForComponent } from 'app/apps/templates-library/sign-up/sign-up-templates-modal.component';
import { AuthService } from 'app/auth/auth.service';
import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { SegmentService } from 'app/segment/segment.service';
import { AppUtilService } from 'app/shared/service/app-util.service';
import { CustomTitleService } from 'app/shared/service/custom-title/custom-title.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { SignUpCompanyBrandingComponent } from 'app/user/sign-up-simplified/sign-up-steps/sign-up-company-branding/sign-up-company-branding.component';
import { SignUpUserSignatureComponent } from 'app/user/sign-up-simplified/sign-up-steps/sign-up-user-signature/sign-up-user-signature.component';
import { SignUpWelcomeMobileComponent } from 'app/user/sign-up-simplified/sign-up-steps/sign-up-welcome-mobile/sign-up-welcome-mobile.component';
import { SignUpService } from 'app/user/sign-up-simplified/sign-up.service';
import { UserService } from 'app/user/user.service';

@Component({
  selector: 'cc-team-home-page',
  templateUrl: 'team-home-page.component.html',
  styleUrls: ['home-page.component.scss'],
})
export class TeamHomePageComponent implements OnInit, OnDestroy {
  unSubscribe = new Subject<void>();

  isSSOSignUp: boolean;
  teamId: string;

  constructor(
    private readonly segmentService: SegmentService,
    private readonly router: Router,
    private readonly location: Location,
    private readonly route: ActivatedRoute,
    private readonly customTitleService: CustomTitleService,
    private readonly userService: UserService,
    private readonly modal: NgbModal,
    private readonly errorHandler: ErrorHandler,
    private readonly appUtilService: AppUtilService,
    private readonly signUpService: SignUpService,
    private readonly i18nService: TmpI18NService,
    private readonly authService: AuthService,
  ) {}

  ngOnInit() {
    this.customTitleService.setTitle(`Activity - Dashpivot`);
    this.signUpRedirection();

    this.route.queryParams
      .pipe(distinctUntilChanged(), takeUntil(this.unSubscribe))
      .subscribe((params: Params) => {
        if (params.fromEmail) {
          this.location.go(this.router.createUrlTree([], { relativeTo: this.route }).toString());
          void EventNotifierService.notify(
            new DashpivotEvent(EventTypes.TeamEmailLinkClicked),
            this.segmentService,
          );
        }
      });
  }

  private signUpRedirection() {
    const user = this.userService.getCurrentUser();
    const isSignupInProgress = user?.signUpProgress?.isComplete === false;

    if (isSignupInProgress && user?.signUpProgress?.step === SignUpSteps.CompanyBranding) {
      this.openCompanyBrandingModal();
    }

    if (isSignupInProgress && user?.signUpProgress?.step === SignUpSteps.Signature) {
      this.openSignatureModal();
    }
  }

  openCompanyBrandingModal(): void {
    const modalRef = this.modal.open(SignUpCompanyBrandingComponent, {
      size: 'lg',
      windowClass: 'modal-400',
      modalDialogClass: 'modal-dialog-centered',
      backdrop: 'static',
      keyboard: false,
      animation: false,
    });
    this.signUpService.popConfetti();

    void modalRef.result
      .then(() => {
        this.openSignatureModal();

        const currentUser = this.userService.getCurrentUser();
        const updatedUser = {
          ...currentUser,
          signUpProgress: {
            ...currentUser.signUpProgress,
            isComplete: false,
            step: SignUpSteps.Signature,
          },
        };

        this.userService.updateProfile(updatedUser).subscribe({
          next: () => {
            this.userService.setCurrentUser(updatedUser);
          },
          error: (error) => this.errorHandler.handle(error),
        });
      })
      .catch(() => {});
  }

  openSignatureModal(): void {
    const signatureModal = this.modal.open(SignUpUserSignatureComponent, {
      size: 'lg',
      windowClass: 'modal-400',
      modalDialogClass: 'modal-dialog-centered',
      backdrop: 'static',
      keyboard: false,
      animation: false,
    });
    const { isMobile } = this.appUtilService;

    void signatureModal.result
      .then(({ signature }) => {
        const currentUser = this.userService.getCurrentUser();
        const updatedUser = {
          ...currentUser,
          signature,
          signUpProgress: {
            ...currentUser.signUpProgress,
            isComplete: true,
            step: SignUpSteps.Signature,
          },
        };

        this.userService.updateProfile(updatedUser).subscribe({
          next: async () => {
            this.signUpService.setProcessing(false);
            this.userService.setCurrentUser(updatedUser);
            if (isMobile) {
              this.openMobileWelcomeModal();
              return;
            }
            await this.setNavigation();
          },
          error: (error) => {
            this.signUpService.setProcessing(false);
            this.errorHandler.handle(error);
          },
        });
      })
      .catch(() => {});
  }

  async setNavigation() {
    const {
      signUpProgress: { templateId = '', teamId = '', isSSOSignUp = false },
    } = this.userService.getCurrentUser();

    this.isSSOSignUp = isSSOSignUp;
    this.teamId = teamId;

    if (this.isSSOSignUp) {
      this.authService.setForceLogoutFlagForSSOSignUp();
    }

    if (templateId) {
      await this.navigateToFormModal(templateId);
    } else {
      await this.navigateToHomeFolder();
    }
  }

  navigateToFormModal(templateId: string) {
    const navigationPath = ['/', 'apps', templateId, 'forms', 'new'];
    const navigationExtras = {
      queryParams: {
        navigateOnSignUpToFormModal: true,
        teamId: this.teamId,
        isSSOSignUp: this.isSSOSignUp ? true : undefined,
      },
    };

    return this.router.navigate(navigationPath, navigationExtras);
  }

  async navigateToHomeFolder() {
    await this.showTemplateSelectorModal(false);

    if (this.isSSOSignUp) {
      const message = this.i18nService.getMessage('ssoRedirectionMessage');
      return this.authService.showRedirectionModal(message);
    }

    return this.router.navigate(['/', 'home']);
  }

  showTemplateSelectorModal(isSingleSelectionOnly: boolean) {
    const { vertical, role } = this.signUpService.getSignUpModel();

    const ref = this.modal.open(SignUpTemplatesModalForComponent, {
      windowClass: 'modal-with-margin',
      backdrop: true,
      keyboard: false,
    });
    ref.componentInstance.businessAttributes = [vertical, role];
    ref.componentInstance.teamId = this.teamId;
    ref.componentInstance.isSingleSelectionOnly = isSingleSelectionOnly;

    return ref.result;
  }

  openMobileWelcomeModal(): void {
    this.modal.open(SignUpWelcomeMobileComponent, {
      fullscreen: true,
    });
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }
}
